import { Reveal, RevealItem, stagger } from "../components/Reveal";
import { Failed, Loading } from "../components/States";
import { Shell } from "../components/Shell";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";

/**
 * Roles, education and certifications in one chronology — PRD §9.
 *
 * The degree renders "2023 – Present", never a bare "2023–2027": a fast
 * skimmer reads the second as a completed degree, and being explicit about
 * student status is more credible than being vague about it
 * (content/education.md display guidance). The server formats it; this page
 * only prints what it was given.
 */
export function Experience() {
  const roles = useApi(() => api.experience(), []);
  const education = useApi(() => api.education(), []);
  const certifications = useApi(() => api.certifications(), []);

  return (
    <Shell className="py-24">
      <p className="mb-2 font-mono text-2xs tracking-widest text-accent uppercase">
        Experience
      </p>
      <h1 className="mb-16 text-xl sm:text-2xl lg:text-3xl">
        Where I&rsquo;ve worked and studied
      </h1>

      <section className="mb-24">
        <h2 className="mb-8 text-lg">Roles</h2>
        {roles.loading && <Loading label="roles" />}
        {roles.error && <Failed error={roles.error} what="the roles" />}
        <ul className="space-y-12">
          {roles.data?.map((role, index) => (
            <RevealItem key={role.slug} delay={stagger(index)}>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <h3 className="text-base text-text">{role.role}</h3>
                {role.is_current && (
                  <span className="font-mono text-2xs text-accent">
                    Current
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-text-dim">
                {role.company}
                {role.date_display && ` · ${role.date_display}`}
                {role.location && ` · ${role.location}`}
              </p>
              {role.description_md && (
                <p className="mt-4 max-w-(--spacing-measure) text-base text-text-dim">
                  {role.description_md}
                </p>
              )}
              {role.highlights.length > 0 && (
                <ul className="mt-4 max-w-(--spacing-measure) list-disc space-y-1.5 pl-5 text-base text-text-dim marker:text-accent-dim">
                  {role.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              )}
            </RevealItem>
          ))}
        </ul>
      </section>

      <section className="mb-24">
        <Reveal>
          <h2 className="mb-8 text-lg">Education</h2>
        </Reveal>
        {education.error && (
          <Failed error={education.error} what="the education entries" />
        )}
        <ul className="space-y-8">
          {education.data?.map((entry, index) => (
            <RevealItem key={entry.slug} delay={stagger(index)}>
              <h3 className="text-base text-text">{entry.degree}</h3>
              <p className="mt-1 text-xs text-text-dim">
                {entry.institution}
                {entry.date_display && ` · ${entry.date_display}`}
                {entry.grade && ` · ${entry.grade}`}
              </p>
              {entry.description_md && (
                <p className="mt-3 max-w-(--spacing-measure) text-base text-text-dim">
                  {entry.description_md}
                </p>
              )}
            </RevealItem>
          ))}
        </ul>
      </section>

      <section>
        <Reveal>
          <h2 className="mb-3 text-lg">Certifications</h2>
          {/* A compact two-column list, not nine cards. Nine cards filling a
              scroll reads as padding; a tight list with the featured ones lifted
              reads as curation (content/certifications.md). */}
          <p className="mb-8 max-w-(--spacing-measure) text-2xs text-text-dim">
            Every entry links to its verification page. An unverifiable
            certification is worth close to nothing.
          </p>
        </Reveal>
        {certifications.error && (
          <Failed error={certifications.error} what="the certifications" />
        )}
        <ul className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {certifications.data?.map((cert, index) => (
            <RevealItem
              key={cert.slug}
              delay={stagger(index)}
              className="border-t border-edge pt-4"
            >
              <p
                className={
                  cert.is_featured ? "text-base text-text" : "text-xs text-text"
                }
              >
                {cert.credential_url ? (
                  <a
                    href={cert.credential_url}
                    className="underline decoration-edge underline-offset-4 transition-colors duration-150 hover:decoration-accent"
                  >
                    {cert.name}
                  </a>
                ) : (
                  cert.name
                )}
              </p>
              <p className="mt-1 text-2xs text-text-dim">
                {cert.issuer}
                {cert.issue_date_display && ` · ${cert.issue_date_display}`}
              </p>
            </RevealItem>
          ))}
        </ul>
      </section>
    </Shell>
  );
}
